/**
 * Shapes and small helpers for the structured bot logs.
 *
 * The backend folds both log streams into the CURRENT STATE of every employee and
 * every file: an employee that failed once and succeeded on a later run arrives as
 * one success that remembers it took two attempts. `resolved` marks exactly those,
 * so the viewer can say "this was fixed" instead of showing a failure that is over.
 */

export type LogStream = "all" | "status" | "file";
export type LogState =
  | "all"
  | "error"
  | "success"
  | "warning"
  | "unknown"
  | "resolved";

export interface LogRecordDto {
  stream: "status" | "file";
  level: string;
  state: "success" | "error" | "warning" | "unknown";
  timestamp: string;
  raw: string;
  source_task_id: number | null;
  employee_name: string | null;
  employee_id: string | null;
  inner_number: number | null;
  employee_status: string | null;
  company: string | null;
  total: number | null;
  downloaded: number | null;
  uploaded_to: string | null;
  uploaded_count: number | null;
  filename: string | null;
  reason: string | null;
  /** What a file-log line said when it names no file (some bots write notes). */
  message: string | null;
  attempts: number;
  resolved: boolean;
  /** Written by a task that has since been deleted from the job. */
  from_deleted_task: boolean;
  first_failed_at: string | null;
  last_failed_at: string | null;
  owner_task_id: number | null;
}

export interface LogStateCounts {
  total: number;
  success: number;
  error: number;
  warning: number;
  unknown: number;
  resolved: number;
}

export interface LogRecordCounts extends LogStateCounts {
  status: LogStateCounts;
  file: LogStateCounts;
  unparsed: number;
  /** Job scope only: records whose employee number no task covers any more. */
  outside_ranges?: number;
  /** Job scope only: records that could not be tied to an employee at all. */
  unattributed?: number;
  /** Records written by tasks the job no longer has - still its work. */
  from_deleted_tasks?: number;
}

export interface LogRecordsRange {
  mode: string | null;
  from: number | null;
  to: number | null;
  merged_from: number | null;
  merged_count: number | null;
  total_employees: number | null;
  notice: string;
  /** Bootstrap bounds or an empty roster: from/to name nobody. */
  ranges_pending?: boolean;
  roster_empty?: boolean;
}

export interface LogRecordsTask {
  id: number;
  from: number | null;
  to: number | null;
  task_status: string;
}

export interface LogRecordsResponse {
  scope: "job" | "task";
  job_id: number | null;
  task_id: number | null;
  counts: LogRecordCounts;
  records: LogRecordDto[];
  total_matched: number;
  offset: number;
  limit: number;
  has_more: boolean;
  source: string | null;
  generated_at: string;
  range: LogRecordsRange;
  tasks: LogRecordsTask[];
}

export interface LogRecordsQuery {
  stream?: LogStream;
  state?: LogState;
  q?: string;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
  refresh?: boolean;
}

export const EMPTY_STATE_COUNTS: LogStateCounts = {
  total: 0,
  success: 0,
  error: 0,
  warning: 0,
  unknown: 0,
  resolved: 0,
};

export function buildLogRecordsQuery(params: LogRecordsQuery): string {
  const search = new URLSearchParams();
  if (params.stream && params.stream !== "all") search.append("stream", params.stream);
  if (params.state && params.state !== "all") search.append("state", params.state);
  if (params.q) search.append("q", params.q);
  if (params.order) search.append("order", params.order);
  if (params.limit != null) search.append("limit", String(params.limit));
  if (params.offset) search.append("offset", String(params.offset));
  if (params.refresh) search.append("refresh", "1");
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/** Same palette as the raw log colorizer, so both views read alike. */
export const LOG_STATE_COLORS: Record<LogRecordDto["state"], string> = {
  success: "success.main",
  error: "error.main",
  warning: "warning.main",
  unknown: "text.disabled",
};

export const LOG_STATE_CHIP_COLOR: Record<
  LogRecordDto["state"],
  "success" | "error" | "warning" | "default"
> = {
  success: "success",
  error: "error",
  warning: "warning",
  unknown: "default",
};

/** Counts for the stream a tab is showing. */
export function countsForStream(
  counts: LogRecordCounts | undefined,
  stream: LogStream,
): LogStateCounts {
  if (!counts) return EMPTY_STATE_COUNTS;
  if (stream === "status") return counts.status ?? EMPTY_STATE_COUNTS;
  if (stream === "file") return counts.file ?? EMPTY_STATE_COUNTS;
  return counts;
}

/** "2026-08-21 12:48:17,315" -> "12:48:17" (the date sits in the tooltip). */
export function formatLogTime(timestamp: string): string {
  const time = timestamp.split(" ")[1] || timestamp;
  return time.split(",")[0] || time;
}

export function formatLogDate(timestamp: string): string {
  return timestamp.split(" ")[0] || "";
}

/** What the row is about: the employee for a status record, the file for a file one. */
export function recordTitle(record: LogRecordDto): string {
  if (record.stream === "file") {
    // Not every file-log line is about a file: some bots write plain notes to
    // that stream, and the note itself is what the row should say.
    return (
      record.filename ||
      record.message ||
      record.employee_name ||
      "(no file name)"
    );
  }
  return record.employee_name || record.employee_id || "(no employee)";
}

/** The line under the title: what happened, in the record's own numbers. */
export function recordSummary(record: LogRecordDto): string {
  if (record.stream === "file") {
    const parts: string[] = [];
    if (record.employee_name) parts.push(record.employee_name);
    if (record.reason && record.reason !== record.message) parts.push(record.reason);
    return parts.join(" - ");
  }
  const parts: string[] = [];
  if (record.total != null) parts.push(`total ${record.total}`);
  if (record.downloaded != null) parts.push(`downloaded ${record.downloaded}`);
  if (record.uploaded_count != null) {
    parts.push(
      record.uploaded_to
        ? `uploaded to ${record.uploaded_to} ${record.uploaded_count}`
        : `uploaded ${record.uploaded_count}`,
    );
  }
  if (record.company) parts.push(record.company);
  return parts.join(" - ");
}
